import ReactToPrint from 'react-to-print'
import React, { useEffect, useState } from 'react'
import { getGroupMamorioQrCodes } from '../../../modules/Rental'
import { isSafari, toId } from '../../../utils/commonUtils'
import { errorIcon } from '../../../icons'
import { useAppDispatch } from '../../../hooks'
import './PrintLabelModal.css'
import { generateRentalDigitCSV } from '../../../helpers/CommonMamorioHelper'

type PropType = {
  group: IGroup
  printables?: IPrintableQr[]
}

const PrintButton = () => (
  <button type="button" className="btn btn-primary btn-sm">
    印刷する
  </button>
)

const toPrintable = (qrCode: IQrCode): IPrintableQr => ({
  id: Number(qrCode.id),
  name: qrCode.resource_name,
  image_url: qrCode.image_url,
  digit_code: qrCode.digit_code,
})

const PrintLabelModal = ({
  group,
  printables,
}: PropType) => {
  const dispatch = useAppDispatch()
  const [printableItems, setPrintableItems] = useState<IPrintableQr[]>(printables ?? [])
  const [downloadableCodes, setDownloadableCodes] = useState<IQrCode[]>([])

  useEffect(() => {
    if (printables) {
      setPrintableItems(printables)
      setDownloadableCodes([])
      return
    }

    if (!group?.id) return

    dispatch(getGroupMamorioQrCodes({
      groupId: toId<GroupId>(group.id),
    }))
      .unwrap()
      .then((qrCodes) => {
        setPrintableItems(qrCodes.map(toPrintable))
        setDownloadableCodes(qrCodes)
      })
      .catch((e) => {
        throw new Error(e.toString())
      })
  }, [dispatch, group, printables])

  let printContentRef: React.ReactInstance | null = null

  return (
    <div className="print-seal">
      <div className="button-section">
        <ReactToPrint
          trigger={PrintButton}
          content={() => printContentRef}
          bodyClass="print-seal"
        />
        {printables == null && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => generateRentalDigitCSV(downloadableCodes)}
          >
            CSVをダウンロード
          </button>
        )}
      </div>
      <div
        className="print-seal-content"
        ref={(el) => {
          printContentRef = el
        }}
      >
        {PrintLabelModal.createPrintContent(printableItems)}
      </div>
    </div>
  )
}

PrintLabelModal.createColumn = (printable: IPrintableQr) => (
  <div className="print-col" key={`${printable.id}-${Date.now()}`}>
    <div className="print-col-title">
      {printable.name}
    </div>
    <div className="print-col-inside">
      <div>
        <img
          src={printable.image_url ?? errorIcon}
          className="print-col-qr"
          alt="qr-code"
        />
      </div>
      <div className="print-col-code">
        <div>
          {(printable.digit_code ?? '').substr(0, 4)}
        </div>
        <div>
          {(printable.digit_code ?? '').substr(4, 4)}
        </div>
      </div>
    </div>
  </div>
)

PrintLabelModal.createPrintContent = (printables: IPrintableQr[]) => {
  const columnsPerRow = 5
  const pageBreakAt = isSafari ? 6 : 6
  const rows = []

  let rowCount = 0
  for (let index = 0; index < printables.length; index += columnsPerRow) {
    const cols = []
    for (let c = index; c < Math.min(index + columnsPerRow, printables.length); c += 1) {
      const printable = printables[c]
      if (printable) {
        cols.push(PrintLabelModal.createColumn(printable))
      }
    }

    rows.push(
      <div className="print-row" key={`print-row-${rowCount}-${printables[index]?.id ?? index}`}>
        {cols}
      </div>,
    )

    rowCount += 1

    if (rowCount % pageBreakAt === 0 && index + columnsPerRow < printables.length) {
      rows.push(
        <footer key={`footer-${rowCount}-${printables[index]?.id ?? index}`} />,
      )
    }
  }

  return rows
}

export default PrintLabelModal
